/**
 * Session view: the transcript-ui viewer fed by a live hub subscription, with the composer
 * (Queue while busy, Stop, attachments, dictation, quick chips), inline question answers and
 * approvals, and the header's Land button. Every action is a signed command.
 */

import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { ApprovalDecision } from '@viko/protocol'
import { isCorrectionMessage } from '@viko/protocol'
import type { Answers, SessionMenuItem } from '@viko/transcript-ui'
import {
  AttachmentDropOverlay,
  Button,
  type ComposerAttachment,
  openAsks,
  Sheet,
  Transcript,
  type UsageLimitActions, // g14-resume
} from '@viko/transcript-ui'
import { Copy, ExternalLink, Play, SquareTerminal } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { StorageNotConfigured } from '../lib/api.js'
import { type ArtifactViewerAccess, useArtifactViewer } from '../lib/artifact-viewer.js'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../lib/attachment-input.js'
import { useBackend } from '../lib/backend.js'
import { copyText, type DevHandle, devHandles, readEditorPref } from '../lib/dev-handles.js'
import { modelName } from '../lib/format.js'
import { homeHref, projectHref, runHref, workstreamHref } from '../lib/ia.js' // ia-shell
import { useLandGrantFor } from '../lib/land-grant.js' // land-grant
import {
  autoLandsOnFinish,
  type LandingStatus,
  landingForRun,
  landingStatus,
} from '../lib/landing-status.js'
import { type PaletteAction, usePaletteActions } from '../lib/palette-actions.js'
import { useProgressSync } from '../lib/progress-snapshot.js'
import { accountUsable } from '../lib/provider-accounts.js' // g14-resume
import { qk, useLandings, useMachines, useProjects, useRun, useTasks } from '../lib/queries.js'
import { useRefStore } from '../lib/refs.js'
import { recordCorrection } from '../lib/routing-prefs.js'
import { runMachine } from '../lib/run-machine.js'
import { useRunWindow } from '../lib/session.js'
import { useDictation } from '../lib/speech.js'
import type { RunSummary } from '../lib/types.js'
import { selectedAccount, selectRowUsage } from '../lib/usage-accounting.js'
import { AttachmentNotices } from './AttachmentDropZone.js'
import { storage } from './Capture.js'
import { EfficiencyRow } from './EfficiencyRow.js'
import { useOneKeyLand } from './LandGrant.js' // land-grant
import { LandingPanel } from './LandSheet.js'
import { LivePreview, PREVIEW_HOST } from './LivePreview.js' // live-preview-tunnel
import { ModelControl, modelSwitchBlock } from './ModelControl.js'
import { RunDetails } from './RunDetails.js'
import { snapshotRenderer } from './SnapshotFrame.js'
import { CostDetails } from './UsageDetails.js'
import { UsageStrip } from './UsageStrip.js'
import { errorText, useToast } from './ui.js'
import { useWide } from './useWide.js'
import { WhyThisAgent } from './WhyThisAgent.js'

/** "Copy branch" → "Branch" (the toast says what was copied). */
const copyWhat = (label: string) => label.replace(/^Copy (\w)/, (_, c: string) => c.toUpperCase())

const COMMANDS = [
  { value: 'compact', hint: 'Summarise and free context' },
  { value: 'review', hint: 'Review the current diff' },
  { value: 'test', hint: 'Run the project test recipe' },
  { value: 'land', hint: 'Ask Viko to land this branch' },
]

export function SessionView({
  runId,
  onBack,
  artifactAccess,
}: {
  runId: string
  onBack?: () => void
  artifactAccess?: ArtifactViewerAccess
}) {
  // phase2-decisions: "Open the decision" under a question card → the decision view.
  const openDecisionNav = useNavigate()
  // ia-shell: the decision opens in its project's tab (Home when the project is unknown).
  const openDecision = (id: string) =>
    void openDecisionNav({
      href: run?.projectId
        ? projectHref(run.projectId, '', `decision:${id}`)
        : homeHref({ i: `decision:${id}` }),
    })
  const b = useBackend()
  const toast = useToast()
  const qc = useQueryClient()
  // materialized-state: snapshot + tail; the newest turns load first, earlier ones on demand.
  const { view, older } = useRunWindow(runId)
  const accountId = b.artifactAccess.accountId
  const store = useMemo(() => b.transcriptStore(runId, accountId), [b, runId, accountId])
  // A deep link to an event (`?seq=N`) loads the window around it.
  useEffect(() => {
    const seq = Number(new URLSearchParams(window.location.search).get('seq'))
    if (Number.isSafeInteger(seq) && seq > 0) void store.focusSeq(seq)
  }, [store])
  const artifactViewer = useArtifactViewer(b.content ? b.artifactAccess : artifactAccess, view)
  const listed = useRun(runId)
  // Imported history isn't in /api/runs; the History tab leaves its row in the cache.
  const run = listed ?? (!b.content ? qc.getQueryData<RunSummary>(['runinfo', runId]) : undefined)
  const readOnly = !!run?.readOnly
  const machines = useMachines()
  const wide = useWide()
  const [draft, setDraft] = useState('')
  const input = useAttachmentInput({
    storageOff: storage.off,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setDraft((cur) => cur + t),
  })
  const [landOpen, setLandOpen] = useState(false)
  const draftAccount = useRef(accountId)
  useEffect(() => {
    if (draftAccount.current === accountId) return
    draftAccount.current = accountId
    setDraft('')
    input.clear()
    setLandOpen(false)
  }, [accountId, input.clear])
  const progressCursor = useMemo(() => {
    try {
      return (
        Number(localStorage.getItem(`viko:progress:${JSON.stringify([accountId, runId])}`)) || 0
      )
    } catch {
      return 0
    }
  }, [accountId, runId])
  useEffect(() => {
    try {
      const key = `viko:progress:${JSON.stringify([accountId, runId])}`
      const previous = Number(localStorage.getItem(key)) || 0
      if (view.lastSeq > previous) localStorage.setItem(key, String(view.lastSeq))
    } catch {
      /* storage unavailable */
    }
  }, [accountId, runId, view.lastSeq])
  const host = useRef<HTMLDivElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const cameraInput = useRef<HTMLInputElement>(null)

  const machineId = run?.machineId ?? ''
  const machine = machines.data?.find((m) => m.id === machineId)
  // openable-refs: paths/commits/previews the agent mentions resolve on the run's machine.
  const refs = useRefStore(b, {
    runId,
    machineId,
    enabled: !readOnly,
    hasArtifact: (id) => !!artifactViewer.artifactById?.(id),
  })
  const snapshotHtml = useMemo(() => snapshotRenderer(), [])
  // ---- live-preview-tunnel: agent localhost links open live, E2E, on the run's machine ----
  const livePreview = useMemo(
    () =>
      PREVIEW_HOST && machineId && !readOnly
        ? ({ url, title, snapshot }: { url: string; title: string; snapshot: () => ReactNode }) => (
            <LivePreview
              backend={b}
              machineId={machineId}
              runId={runId}
              projectId={run?.projectId}
              url={url}
              title={title}
              platform={machine?.platform}
              online={machine?.online ?? false}
              snapshot={snapshot}
            />
          )
        : undefined,
    [b, machineId, readOnly, runId, run?.projectId, machine?.platform, machine?.online],
  )
  // ---- end live-preview-tunnel ----
  const tasks = useTasks({ ...(run?.workstreamId ? { workstreamId: run.workstreamId } : {}) })
  const usage = useMemo(() => selectRowUsage(run ?? {}, view), [run, view])
  const account = selectedAccount(run, machines.data)

  // Dictation appends to whatever is already typed (the composer is controlled).
  const dictation = useDictation((text) =>
    setDraft((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${text}` : text)),
  )

  const act = useCallback(
    async <T,>(fn: () => Promise<T>, ok?: string) => {
      try {
        const r = await fn()
        if (ok) toast(ok)
        return r
      } catch (e) {
        if (e instanceof StorageNotConfigured) storage.off = true
        toast(errorText(e), 'error')
        return undefined
      }
    },
    [toast],
  )

  const send = async (text: string, { queue }: { queue: boolean }) => {
    if (!machineId) return toast('This run has no machine yet.', 'error')
    if (!input.canSend) {
      // The composer clears itself after onSend: put the text back once it has.
      setTimeout(() => setDraft(text), 0)
      return toast('Remove or fix the attachments marked with an error first.', 'error')
    }
    const pending = input.files
    input.markUploading(true)
    const ok = await act(async () => {
      const attachments = pending.length
        ? await b.uploadAttachments(pending, runId, machineId)
        : undefined
      await b.dispatch('run.message', machineId, {
        runId,
        text,
        queue,
        clientMessageId: crypto.randomUUID(),
        ...(attachments ? { attachments } : {}),
      })
      return true
    })
    if (!ok) {
      // Keep what the user wrote (the composer cleared itself on send).
      setDraft(text)
      input.retryable()
      input.markUploading(false)
    } else {
      input.clear()
      // ---- g11-routing: a redirect/undo/fix-up counts against the provider that needed it ----
      if (b.entities && isCorrectionMessage(text))
        void recordCorrection(b.entities, runId).catch(() => {})
      // ---- end g11-routing ----
    }
  }

  // Asks can stream in before the run list has this run: resolve its machine on demand.
  const target = () => runMachine(machineId, runId, () => b.api.runs())

  const stop = () =>
    act(
      async () => b.dispatch('run.interrupt', await target(), { runId, cancelQueued: true }),
      'Stopped',
    )

  const answer = (questionId: string, answers: Answers) =>
    act(
      async () => b.dispatch('run.answer', await target(), { runId, questionId, answers }),
      'Answer sent',
    )

  const decide = (approvalId: string, decision: ApprovalDecision, message?: string) =>
    act(
      async () =>
        b.dispatch('run.approve', await target(), {
          runId,
          approvalId,
          decision,
          ...(message ? { message } : {}),
        }),
      decision === 'deny' ? 'Denied' : decision === 'cancel' ? 'Cancelled' : 'Approved',
    )

  // ---- g14-resume: usage-limit drill-down actions (signed limit.* commands) ----
  const limitProvider = view.usageLimit?.provider
  const usageLimitActions: UsageLimitActions = {
    onResumeNow: (parkId) =>
      void act(async () => b.dispatch('limit.resume_now', await target(), { parkId }), 'Resuming'),
    onSwitchAccount: (parkId, accountId) =>
      void act(
        async () => b.dispatch('limit.switch_account', await target(), { parkId, accountId }),
        'Continuing on the other account',
      ),
    accounts: (machine?.providerAccounts ?? [])
      .filter((a) => a.provider === limitProvider && accountUsable(a))
      .map((a) => ({ id: a.id, label: a.label })),
  }
  // ---- end g14-resume ----

  // ---- landing (async): Land queues and returns; a status chip tracks it -------------------
  const projects = useProjects()
  const landings = useLandings(run?.projectId)
  const projectTasks = useTasks({ ...(run?.projectId ? { projectId: run.projectId } : {}) })
  // Just queued from here: until the daemon's report for it arrives, show "Landing queued".
  const [queued, setQueued] = useState<{ prevId: string | undefined; at: number } | undefined>()
  const [now, setNow] = useState(() => Date.now())
  const git = view.progress.git as { headSha?: string; ahead?: number } | undefined
  const headSha = git?.headSha
  const landingNow = landingForRun(landings.data, run)
  const ahead = git?.ahead ?? run?.ahead
  const waitingForReport = !!queued && landingNow?.id === queued.prevId && now - queued.at < 120_000
  const reported = landingStatus({
    ...(landingNow ? { landing: landingNow } : {}),
    autoLand: autoLandsOnFinish(projects.data?.find((p) => p.id === run?.projectId)),
    ...(headSha ? { headSha } : {}),
    ...(ahead !== undefined ? { ahead } : {}),
    ...(view.progress.landed?.sha ? { landedSha: view.progress.landed.sha } : {}),
    legacy: run?.landing,
    now,
  })
  const landStatus: LandingStatus =
    waitingForReport && !['queued', 'verifying', 'landed'].includes(reported.phase)
      ? {
          phase: 'queued',
          chip: { label: 'Landing queued', tone: 'run' },
          showLand: false,
          landLabel: 'Land',
        }
      : reported
  const ticking = landStatus.phase === 'verifying' || landStatus.phase === 'queued'
  useEffect(() => {
    if (!ticking) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [ticking])

  // ---- land-grant: a standing grant lands without a tap (never "land anyway") ----
  const landGrant = useLandGrantFor(run?.projectId, machineId)
  // ---- end land-grant ----
  const land = async (opts: { acceptBaselineFailures?: boolean } = {}) => {
    if (!run?.workstreamId) return
    const r = await act(
      () =>
        b.dispatch(
          'land.merge',
          machineId,
          {
            workstreamId: run.workstreamId as string,
            runId,
            ...(run.branch ? { branch: run.branch } : {}),
            target: 'main',
            ...(opts.acceptBaselineFailures ? { acceptBaselineFailures: true } : {}),
          },
          {
            summary: opts.acceptBaselineFailures
              ? `Land ${run.branch ?? 'this branch'} into main despite checks that fail on main too`
              : `Land ${run.branch ?? 'this branch'} into main`,
            ...(landGrant && !opts.acceptBaselineFailures ? { landGrant: landGrant.proof } : {}),
          },
        ),
      'Landing queued',
    )
    if (r) {
      const at = Date.now()
      setNow(at)
      setQueued({ prevId: landingNow?.id, at })
      void qc.invalidateQueries({ queryKey: ['landings'] })
    }
  }

  const cancelLanding = (landingId: string) =>
    act(async () => {
      await b.dispatch('land.cancel', machineId, { landingId })
      setQueued(undefined)
      void qc.invalidateQueries({ queryKey: ['landings'] })
    }, 'Landing cancelled')

  const fixTask = (command: string) =>
    act(async () => {
      await b.api.createTask({
        ...(run?.projectId ? { projectId: run.projectId } : {}),
        title: `Fix ${command} (fails on main too)`,
        description: `The landing recipe step \`${command}\` fails on the target branch as well, so landings are blocked for every branch.`,
      })
      void qc.invalidateQueries({ queryKey: ['tasks'] })
    }, 'Fix task created')

  const addFiles = input.add

  const attachments: ComposerAttachment[] = input.items
  // Reload: the sealed last-known state renders at once; replay swaps in when it catches up.
  const sync = useProgressSync(runId, view, store, run?.headSeq)
  // The run-state pill reads the same landing truth as the landing chip.
  const landState =
    landStatus.phase === 'landed'
      ? ('landed' as const)
      : landStatus.phase === 'queued' || landStatus.phase === 'verifying'
        ? ('running' as const)
        : landStatus.phase === 'blocked'
          ? ('blocked' as const)
          : undefined
  const busy =
    view.status === 'running' ||
    view.status === 'waiting' ||
    view.status === 'compacting' ||
    view.awaiting > 0

  // ---- ui-p0: developer handles (⋯ menu, click-to-copy chips) and ⌘K context actions ----
  const copyHandle = async (what: string, value: string) =>
    toast((await copyText(value)) ? `${what} copied` : `${what}: ${value}`)
  const handles = useMemo(
    () =>
      run
        ? devHandles(run, {
            ...((headSha ?? view.progress.landed?.sha ?? run.landing?.sha)
              ? { sha: headSha ?? view.progress.landed?.sha ?? run.landing?.sha }
              : {}),
            ...(readEditorPref() ? { editorPref: readEditorPref() } : {}),
            // viko-cli: the matching `viko` command
            runId,
            live: busy,
            canLand: !!run.workstreamId && !readOnly && landStatus.showLand,
          })
        : [],
    [run, headSha, view.progress.landed?.sha, runId, busy, readOnly, landStatus.showLand],
  )
  const menu: SessionMenuItem[] = handles.map((h) => ({
    id: h.id,
    label: h.label,
    ...(h.hint ? { hint: h.hint } : {}),
    ...(h.href ? { href: h.href } : {}),
    ...(h.copy ? { onSelect: () => void copyHandle(copyWhat(h.label), h.copy as string) } : {}),
  }))
  // app-clarity run header: Take over in the editor (o) and Terminal (copy the resume line, t)
  // sit beside the title; Land / Retry land stays the one primary when the branch can land.
  const editor = handles.find(
    (h): h is DevHandle & { href: string } => h.id.startsWith('editor') && !!h.href,
  )
  const resume = handles.find((h) => h.id === 'resume' && h.copy)
  const takeOver =
    !readOnly && (editor || resume) ? (
      <>
        {editor ? (
          <a
            className={`vk-btn vk-btn--sm ${landStatus.showLand && run?.workstreamId ? 'vk-btn--secondary' : 'vk-btn--primary'}`}
            href={editor.href}
            aria-keyshortcuts="o"
            title={`${editor.label}${machine ? ` on ${machine.name}` : ''} (o)`}
          >
            <ExternalLink size={14} aria-hidden="true" />
            <span>
              Take over{machine ? <span className="vk-wide-only"> on {machine.name}</span> : null}
            </span>
          </a>
        ) : null}
        {resume ? (
          <button
            type="button"
            className="vk-btn vk-btn--sm vk-btn--secondary vk-wide-only"
            aria-keyshortcuts="t"
            title={`Copy the terminal resume command (t): ${resume.copy}`}
            onClick={() => void copyHandle('Resume command', resume.copy as string)}
          >
            <SquareTerminal size={14} aria-hidden="true" />
            <span>Terminal</span>
          </button>
        ) : null}
      </>
    ) : null
  const parent =
    run?.workstreamId && run.projectId && run.workstreamName ? (
      <>
        Part of{' '}
        <Link to={workstreamHref(run.workstreamId, run.projectId)}>{run.workstreamName}</Link>
      </>
    ) : undefined
  const approval = useMemo(
    () => (readOnly ? undefined : openAsks(view).find((a) => a.type === 'approval')),
    [view, readOnly],
  )
  const canLand = !!run?.workstreamId && !readOnly && landStatus.showLand
  // The palette keeps its list while the handlers change every render: it calls the latest ones.
  const handlers = useRef({ land, decide, stop, copyHandle })
  handlers.current = { land, decide, stop, copyHandle }
  const paletteActions = useMemo<PaletteAction[]>(() => {
    const out: PaletteAction[] = []
    const title = run?.title ?? runId
    if (canLand)
      out.push({
        id: 'session.land',
        label: `${landStatus.landLabel}: ${run?.branch ?? title}`,
        keywords: 'land merge ship',
        run: () => void handlers.current.land(),
      })
    if (approval?.type === 'approval') {
      const what = approval.title ?? approval.toolName ?? 'the request'
      out.push(
        {
          id: 'session.allow',
          label: `Allow once: ${what}`,
          keywords: 'approve allow yes',
          run: () => void handlers.current.decide(approval.approvalId, 'allow'),
        },
        {
          id: 'session.deny',
          label: `Deny: ${what}`,
          keywords: 'reject deny no',
          run: () => void handlers.current.decide(approval.approvalId, 'deny'),
        },
      )
    }
    if (busy && !readOnly)
      out.push({
        id: 'session.stop',
        label: `Stop: ${title}`,
        keywords: 'stop interrupt cancel',
        run: () => void handlers.current.stop(),
      })
    for (const h of handles)
      out.push({
        id: `session.${h.id}`,
        label: h.label,
        ...(h.hint ? { hint: h.hint } : {}),
        keywords:
          h.id === 'branch' ? 'yank branch' : h.id.startsWith('editor') ? 'editor code' : '',
        run: h.href
          ? () => window.location.assign(h.href as string)
          : () => void handlers.current.copyHandle(copyWhat(h.label), h.copy as string),
      })
    return out
  }, [
    canLand,
    approval,
    busy,
    readOnly,
    handles,
    landStatus.landLabel,
    run?.branch,
    run?.title,
    runId,
  ])
  usePaletteActions(`session:${runId}`, paletteActions)
  // ---- end ui-p0 ----
  // ---- land-grant: one-key land (L); within a grant it needs no passkey tap ----
  useOneKeyLand(canLand && !landOpen, () => void handlers.current.land())
  // ---- end land-grant ----

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: paste/drop target for screenshots and files
    <div
      ref={host}
      className="relative flex h-full min-h-0 min-w-0 flex-col"
      {...input.dropProps}
      onPaste={(e) => {
        // The composer handles its own pastes; this catches screenshots pasted elsewhere.
        if (!(e.target as HTMLElement).closest('input, textarea, [contenteditable="true"]'))
          input.onPaste(e)
      }}
    >
      <AttachmentDropOverlay active={input.dragging} />
      {readOnly && run ? <ReadOnlyBanner run={run} machineName={machine?.name} /> : null}
      <Transcript
        key={runId}
        defaultView="progress"
        progressCursor={progressCursor}
        sync={sync}
        lifecycle={{
          ended: run?.status === 'done',
          ...(landState ? { landing: landState } : {}),
        }}
        className="min-h-0 flex-1"
        view={view}
        usageLimit={usageLimitActions} // g14-resume
        {...(older ? { older } : {})}
        context={{
          wide,
          ...artifactViewer,
          ...(refs ? { refs, ...(snapshotHtml ? { renderHtml: snapshotHtml } : {}) } : {}),
          ...(refs && livePreview ? { renderLivePreview: livePreview } : {}), // live-preview-tunnel
          // auto-artifacts: publish an offered deliverable on the run's machine
          ...(machineId
            ? {
                publishArtifact: (path: string) =>
                  b.dispatch('run.publish_artifact', machineId, { runId, path }),
              }
            : {}),
        }}
        header={{
          title: run?.title ?? runId,
          ...(run?.gitBranch && !run.branch ? { branch: run.gitBranch } : {}),
          ...(modelName(view.session.model) ? { model: modelName(view.session.model) } : {}),
          ...(run?.projectName ? { project: run.projectName } : {}),
          ...(machine
            ? {
                machine: {
                  name: machine.name,
                  kind:
                    machine.platform === 'mac'
                      ? 'mac'
                      : machine.platform === 'windows'
                        ? 'windows'
                        : 'linux',
                },
              }
            : {}),
          ...(run?.branch ? { branch: run.branch } : {}),
          ...(run?.worktree ? { worktree: run.worktree } : {}),
          ...(run?.ahead != null ? { ahead: run.ahead } : {}),
          ...(run?.behind != null ? { behind: run.behind } : {}),
          ...(run?.workstreamId && !readOnly
            ? {
                ...(landStatus.showLand
                  ? {
                      land: {
                        state: 'ready' as const,
                        // land-grant: the label says when no passkey tap is needed.
                        label: landGrant
                          ? `${landStatus.landLabel} · within grant`
                          : landStatus.landLabel,
                      },
                      onLand: () => void land(),
                    }
                  : {}),
                ...(landStatus.chip
                  ? { landStatus: landStatus.chip, onLandStatus: () => setLandOpen(true) }
                  : {}),
              }
            : {}),
          ...(onBack ? { onBack } : {}),
          ...(parent ? { parent } : {}),
          ...(takeOver ? { actions: takeOver } : {}),
          ...(run?.startedAt ? { startedAt: run.startedAt } : {}),
          // Dev handles: chips copy on click; the ⋯ menu copies / opens in the editor.
          onCopy: (what: string, value: string) => void copyHandle(what, value),
          ...(menu.length ? { menu } : {}),
          // Account, cost, headroom and "why this agent": one drillable row under the title.
          extra: (
            <RunDetails usage={usage} {...(account ? { account } : {})}>
              {/* ---- g11-routing: why this agent ---- */}
              {run && !readOnly ? (
                <WhyThisAgent run={run} {...(machine?.name ? { machineName: machine.name } : {})} />
              ) : null}
              {/* ---- end g11-routing ---- */}
              {/* g18: why this model (the planner's pick, a project hint or the task type) */}
              {view.session.modelReason ? (
                <p className="m-0 px-4 py-1 text-[14px] text-text-2" data-testid="model-reason">
                  Model: {view.session.modelReason}
                </p>
              ) : null}
              <EfficiencyRow efficiency={run?.usageReport?.efficiency} />
              <UsageStrip
                run={{
                  ...run,
                  ...(run?.taskId
                    ? { taskTitle: tasks.data?.find((t) => t.id === run.taskId)?.title }
                    : {}),
                }}
                usage={usage}
                awaiting={view.awaiting}
                {...(account ? { account } : {})}
                {...(modelName(view.session.model) ? { model: modelName(view.session.model) } : {})}
                compact={!wide}
              />
            </RunDetails>
          ),
          ...(b.content && run && !readOnly
            ? {
                onRename: (title: string) => {
                  b.renameRun(runId, title)
                    .then(() => qc.invalidateQueries({ queryKey: qk.runs }))
                    .catch((e) => toast(errorText(e), 'error'))
                },
              }
            : {}),
          costDetails: (
            <CostDetails
              usage={usage}
              now={Date.now()}
              {...(modelName(view.session.model) ? { model: modelName(view.session.model) } : {})}
            />
          ),
          compact: wide,
        }}
        handlers={
          readOnly
            ? { onOpenDecision: openDecision }
            : { onAnswer: answer, onDecide: decide, onOpenDecision: openDecision }
        }
        composer={
          readOnly
            ? false
            : {
                attachments,
                value: draft,
                onChange: setDraft,
                // Quick replies fit the run: "land it" only when this branch can land.
                chips: canLand ? ['continue', 'land it', 'status?'] : ['continue', 'status?'],
                // Narrow + busy: Stop and Queue need the room, so the model chip steps aside.
                model: !wide && busy ? '' : (modelName(view.session.model) ?? ''),
                ...(view.session.model && machineId && (wide || !busy)
                  ? {
                      modelSlot: (
                        <ModelControl
                          runId={runId}
                          machineId={machineId}
                          agent={view.session.agent}
                          model={view.session.model}
                          effort={view.session.effort}
                          cliModels={view.session.models}
                          disabledReason={modelSwitchBlock({
                            machineId,
                            machineOnline: machine?.online,
                            status: run?.status,
                          })}
                        />
                      ),
                    }
                  : {}),
                commands: COMMANDS,
                dictating: dictation.listening,
                onSend: (t, o) => void send(t, o),
                onStop: () => void stop(),
                onAttach: () => fileInput.current?.click(),
                onCamera: () => cameraInput.current?.click(),
                onDictate: dictation.supported
                  ? dictation.toggle
                  : () => toast('Dictation is not available in this browser.', 'error'),
                onRemoveAttachment: input.remove,
                onPaste: input.onPaste,
              }
        }
      />
      {input.notices.length || input.largePaste != null ? (
        <div className="mx-4 mb-2">
          <AttachmentNotices input={input} />
        </div>
      ) : null}
      {dictation.listening && dictation.interim ? (
        <div className="vk-dictation mx-4 mb-2" role="status">
          <span className="vk-dictation__text">{dictation.interim}</span>
        </div>
      ) : null}
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          addFiles(e.target.files)
          e.target.value = ''
        }}
      />
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          addFiles(e.target.files)
          e.target.value = ''
        }}
      />
      {landOpen && run ? (
        <LandingPanel
          status={landStatus}
          {...(landings.data ? { landings: landings.data } : {})}
          {...(landGrant && run?.projectId
            ? { grant: { expiresAt: landGrant.expiresAt, projectId: run.projectId } }
            : {})}
          tasks={projectTasks.data ?? []}
          now={now}
          onClose={() => setLandOpen(false)}
          onCancel={(id) => void cancelLanding(id)}
          onRetry={() => {
            setLandOpen(false)
            void land()
          }}
          onLandAnyway={() => {
            setLandOpen(false)
            void land({ acceptBaselineFailures: true })
          }}
          onFixTask={(cmd) => void fixTask(cmd)}
        />
      ) : null}
    </div>
  )
}

/**
 * Read-only sessions: an **adopted** terminal session (streamed live while someone types in the
 * terminal) or **imported** history. Continue it in Viko with a signed `run.start
 * {resumeNativeSessionId}` (the hub prepares it, this device signs), or copy the terminal line.
 */
function ReadOnlyBanner({ run, machineName }: { run: RunSummary; machineName?: string }) {
  const b = useBackend()
  const toast = useToast()
  const nav = useNavigate()
  const [resuming, setResuming] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const live = run.typingElsewhere
  const copy = async () => {
    if (!run.resumeCommand) return
    try {
      await navigator.clipboard.writeText(run.resumeCommand)
      toast('Resume command copied')
    } catch {
      toast(run.resumeCommand)
    }
  }
  const resume = async () => {
    setBusy(true)
    try {
      const { command } = await b.api.resumeRun(run.runId, prompt.trim() || undefined)
      await b.signPrepared(command, { summary: 'Resume this session in Viko' })
      const next = (command.payload as { runId?: string }).runId
      setResuming(false)
      toast('Resumed in Viko')
      if (next) void nav({ href: runHref(next, run.projectId) }) // ia-shell
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className={`readonly-banner${live ? ' is-live' : ''}`} role="status">
      <span className="readonly-banner__text">
        {live ? (
          <>
            <strong>Typing elsewhere.</strong> This session is running in a terminal
            {machineName ? ` on ${machineName}` : ''}; Viko follows it live, read-only.
          </>
        ) : (
          <>
            <strong>{run.source === 'adopted' ? 'Terminal session' : 'Imported history'}.</strong>{' '}
            Read-only here; resume it to keep going.
          </>
        )}
      </span>
      <span className="flex flex-none flex-wrap gap-1.5">
        {run.resumeCommand ? (
          <Button size="sm" variant="ghost" icon={<Copy size={13} />} onClick={() => void copy()}>
            Copy resume command
          </Button>
        ) : null}
        {!live && run.nativeSessionId ? (
          <Button
            size="sm"
            variant="primary"
            icon={<Play size={13} />}
            onClick={() => setResuming(true)}
          >
            Resume in Viko
          </Button>
        ) : null}
      </span>
      {resuming ? (
        <Sheet
          open
          onClose={() => setResuming(false)}
          title="Resume in Viko"
          subtitle={run.title}
          actions={
            <>
              <Button variant="ghost" onClick={() => setResuming(false)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void resume()}>
                {busy ? 'Resuming…' : 'Resume'}
              </Button>
            </>
          }
        >
          <label className="field pb-2">
            <span>First message (optional)</span>
            <textarea
              className="input"
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Pick up where we left off"
            />
          </label>
        </Sheet>
      ) : null}
    </div>
  )
}
